/* EditableTextControlRenderer.js */

"use strict";

const _ = require("lodash");

class EditableTextControlRenderer {
    constructor(model) {
        this._model = model;
        this._rootElem = document.createElement("editable-text-control");
        this._rootElem.tabIndex = 0;
        this._editIcon = document.createElement('div');
        this._editIcon.classList.add('edit-icon');
        this._rootElem.appendChild(this._editIcon);
        this._rootElem.component = this._model;

        this._registerListeners();
        this.render();
    }
    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/
    _shutdown(){
        let prom = new Promise((resolve /*,reject*/) => {
            this._unregisterListeners();
            this._model = undefined;
            this._rootElem.remove();
            this._editIcon = undefined;
            this._rootElem = undefined;
            resolve();
        });
        return prom;
    }
    _registerListeners(){
        this._model.on("Editing",this._onEditing = this._onEditing.bind(this));
        this._model.on("Select",this._onSelect = this._onSelect.bind(this));
        this._model.on("Disabled",this._onDisabled = this._onDisabled.bind(this));
        this._model.on("Value",this._onValue = this._onValue.bind(this));
        this._model.on("Visible",this._onVisible = this._onVisible.bind(this));
        this._model.on("Invalid",this._onInvalid = this._onInvalid.bind(this));
        this._model.on("Placeholder",this._onPlaceholder = this._onPlaceholder.bind(this));
        this._model.on("DarkTheme",this._onDarkTheme = this._onDarkTheme.bind(this));
        this._model.on("Focus",this._onFocus = this._onFocus.bind(this));
        this._model.on("Blur",this._onBlur = this._onBlur.bind(this));
        this._model.on("SetAttribute",this._onSetAttribute = this._onSetAttribute.bind(this));
        this._rootElem.addEventListener('click', this._onClickEvent = this._onClickEvent.bind(this));
        this._rootElem.addEventListener("keypress", this._onKeyPressEvent = this._onKeyPressEvent.bind(this));
        this._rootElem.addEventListener("input", this._onInputEvent = this._onInputEvent.bind(this));
        this._rootElem.addEventListener('focus', this._onFocusEvent = this._onFocusEvent.bind(this));
        this._rootElem.addEventListener('blur', this._onBlurEvent = this._onBlurEvent.bind(this));
        this._rootElem.addEventListener('keydown', this._onKeyDownEvent = this._onKeyDownEvent.bind(this));
    }
    _unregisterListeners(){
        this._model.removeListener("Editing",this._onEditing);
        this._model.removeListener("Select",this._onSelect);
        this._model.removeListener("Disabled",this._onDisabled);
        this._model.removeListener("Value",this._onValue);
        this._model.removeListener("Visible",this._onVisible);
        this._model.removeListener("Invalid",this._onInvalid);
        this._model.removeListener("Placeholder",this._onPlaceholder);
        this._model.removeListener("DarkTheme",this._onDarkTheme);
        this._model.removeListener("Focus",this._onFocus);
        this._model.removeListener("Blur",this._onBlur);
        this._model.removeListener("SetAttribute",this._onSetAttribute);
        this._rootElem.removeEventListener('click', this._onClickEvent);
        this._rootElem.removeEventListener("keypress", this._onKeyPressEvent);
        this._rootElem.removeEventListener("input", this._onInputEvent);
        this._rootElem.removeEventListener('focus', this._onFocusEvent);
        this._rootElem.removeEventListener('blur', this._onBlurEvent);
        this._rootElem.removeEventListener('keydown', this._onKeyDownEvent);
    }
    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/
    /**
     * Stateless method to completely rebuild this component from the _rootElem down.
     * @returns {Promise} An ES6 promise that lets the caller know when the component has finished rendering. If a non
     * Promise is returned the caller will assume the Renderer has completed rendering.
     */
    render(){
        let prom = new Promise((resolve/*,reject*/) => {
            this._setEditing(this._model.editing);
            this._setDisabled(this._model.disabled);
            this._setValue(this._model.value);
            this._setVisible(this._model.visible);
            this._setInvalid(this._model.invalid);
            this._setPlaceholder(this._model.placeholder);
            this._setDarkTheme(this._model.darkTheme);
            resolve();
        });
        return prom;
    }
    get rootElement(){
        return this.getRootElement();
    }
    getRootElement(){
        return this._rootElem;
    }
    get model(){
        return this.getModel();
    }
    getModel(){
        return this._model;
    }
    /*******************************************/
    /* END COMPONENT RENDERER METHODS */
    /* START HANDLER METHODS */
    /*******************************************/

    /**
     * Fired when the control or edit icon has been clicked
     * @param newVal {Boolean}
     * @private
     */
    _onEditing(newVal){
        this._setEditing(newVal);
    }

    /**
     * Fired when the select method has been called in the model
     * @private
     */
    _onSelect(){
        this._setSelected();
    }

    /**
     * Fired when the disabled state has been updated in the model
     * @param newVal {Boolean}
     * @private
     */
    _onDisabled(newVal){
        this._setDisabled(newVal);
    }

    /**
     * Fired when the invalid state has been updated in the model
     * @param newVal {Boolean}
     * @private
     */
    _onInvalid(newVal){
        this._setInvalid(newVal);
    }

    /**
     * Fired when the value has been updated in the model
     * @param newVal {String}
     * @param oldVal {String}
     * @private
     */
    _onValue(newVal /*, oldVal*/){
        this._setValue(newVal);
    }

    /**
     * Fired when the visible state has been updated in the model
     * @param newVal {Boolean}
     * @private
     */
    _onVisible(newVal){
        this._setVisible(newVal);
    }

    /**
     * Fired when the placeholder has been updated in the model
     * @param newVal {String}
     * @private
     */
    _onPlaceholder(newVal){
        this._setPlaceholder(newVal);
    }

    /**
     * Fired when the darkTheme has been updated in the model
     * @param newVal {Boolean}
     * @private
     */
    _onDarkTheme(newVal){
        this._setDarkTheme(newVal);
    }

    /**
     * Fired when focus has been called in the model
     * @private
     */
    _onFocus(){
        this._setFocus(true);
    }

    /**
     * Fired when blur has been called in the model
     * @private
     */
    _onBlur(){
        this._setFocus(false);
    }

    /**
     * Fired when setAttribute has been called in the model
     * @param attrName {String} - The name of the HTML attribute to be set on the native control
     * @param attrValue {String|Undefined} - The value of the HTML attribute to be set on the native control
     * @private
     */
    _onSetAttribute(attrName, attrValue){
        this._setAttribute(attrName, attrValue);
    }

    /**
     * Make the control editable if it isn't disabled.
     * @param event {Event}
     * @private
     */
    _onClickEvent(/*event*/) {
        this._checkEditingState();
    }

    /**
     * Event handler for the 'keypress' event. Used to blur the element when the enter key is pressed.
     * @param event {Event}
     * @private
     */
    _onKeyPressEvent(event) {
        let charCode = event.charCode || event.which;
        if (charCode === 13) {
            this._checkValue(event);
        }
    }

    /**
     * Apply placeholder text and classes to the DOM element.
     * @param event {Event}
     * @private
     */
    _onInputEvent(/*event*/) {
        this._setPlaceholder(this._model.placeholder);
    }

    /**
     * Pass the focus event up to the model
     * @param event {Event}
     * @private
     */
    _onFocusEvent(event){
        this._model._triggerUserFocus(event);
    }

    /**
     * Make the control read-only.
     * @param event {Event}
     * @private
     */
    _onBlurEvent(event) {
        this._checkValue(event);
        this._model._triggerUserBlur(event);
    }

    /**
     * Pass the blur event up to the model
     * @param event {Event}
     * @private
     */
    _onKeyDownEvent(event){
        this._model._triggerUserKeyDown(event);
    }

    /*******************************************/
    /* END HANDLER METHODS */
    /* START PRIVATE API METHODS */
    /*******************************************/

    /**
     * Set the text inside of the control and add the editable icon.
     * @param {String} newVal - String representing the text to set inside of the control.
     * @private
     */
    _setInnerHTML(newVal){
        let innerText = document.createTextNode(newVal);
        this._removeDOMContents();
        this._rootElem.innerHTML = '';
        this._rootElem.appendChild(innerText);
        this._rootElem.appendChild(this._editIcon);
    }

    /**
     * Clear out the inner contents of the root element
     * @private
     */
    _removeDOMContents(){
        let child = this._rootElem.firstChild;
        while (child) {
            this._rootElem.removeChild(child);
            child = this._rootElem.firstChild;
        }
    }

    /**
     * Make the control read-only and update the value of the control, if necessary.
     * @param domEvent {Event}
     * @private
     */
    _checkValue(domEvent){
        if (this._model.value !== this._rootElem.innerText) {
            let oldVal = _.cloneDeep(this._model.value);
            let newVal = this._rootElem.innerText;
            this._model.setValue(this._rootElem.innerText, true);
            this._model._triggerUserValue(newVal, oldVal, domEvent);
        }
        this._setEditing(false);
    }

    /**
     * Make the control editable.
     * @private
     */
    _checkEditingState(){
        if (!this._model._editing && !this._model._disabled) {
            this._setEditing(true);
        }
    }

    /**
     * Toggle the editing state
     * @param newVal {Boolean}
     * @private
     */
    _setEditing(newVal){
        if (!this._model.disabled) {
            if (newVal) {
                this._model._editing = true;
                this._rootElem.setAttribute("contentEditable", true);
                this._rootElem.focus();
                let selection = window.getSelection();
                if (selection.rangeCount > 0) {
                    selection.collapse(this._rootElem, 1);
                }
            } else {
                this._model._editing = false;
                this._rootElem.removeAttribute("contentEditable");
                this._rootElem.blur();
            }
            this._setPlaceholder(this._model.placeholder);
        }
    }

    /**
     * Select the text range inside of the control
     * @private
     */
    _setSelected(){
        if (this._model.editing && !this._model.disabled) {
            let range = document.createRange();
            range.selectNodeContents(this._rootElem);
            let sel = window.getSelection();
            sel.removeAllRanges();
            sel.addRange(range);
        }
    }

    /**
     * Toggle the disabled state
     * @param newVal {Boolean}
     * @private
     */
    _setDisabled(newVal){
        if (newVal === true) {
            this._rootElem.setAttribute('disabled', true);
            this._rootElem.tabIndex = -1;
        } else {
            this._rootElem.removeAttribute('disabled');
            this._rootElem.tabIndex = 0;
        }
    }

    /**
     * Toggle the invalid state
     * @param newVal {Boolean}
     * @private
     */
    _setInvalid(newVal){
        if (newVal === true) {
            this._rootElem.classList.add('invalid');
        } else {
            this._rootElem.classList.remove('invalid');
        }
    }

    /**
     * Toggle the display text value of the button
     * @param newVal {String}
     * @private
     */
    _setValue(newVal){
        if (newVal !== undefined && newVal !== null && newVal !== "") {
            this._setInnerHTML(newVal);
        } else {
            this._setInnerHTML(this._model.placeholder);
        }
    }

    /**
     * Toggle the visible state
     * @param newVal {Boolean}
     * @private
     */
    _setVisible(newVal){
        if (newVal === true) {
            this._rootElem.classList.remove('hidden');
        } else {
            this._rootElem.classList.add('hidden');
        }
    }

    /**
     * Put the placeholder text in place and move the cursor to the front of the selection if placeholder text
     * is displaying.
     * @param newVal {String}
     * @private
     */
    _setPlaceholder(newVal){
        let addPlaceholderText = (text) => {
            this._setInnerHTML(text);
            this._rootElem.classList.add("placeholder");
        };
        this._rootElem.classList.remove("placeholder");
        // No inner text at all; present the placeholder text
        if (this._rootElem.innerText.length === 0) {
            addPlaceholderText(newVal);
        } else if (this._rootElem.innerText.indexOf(newVal) !== -1) {
            // The user added a character when the placeholder text was displaying; remove placeholder text
            if (this._rootElem.innerText.length > newVal.length) {
                this._setInnerHTML(this._rootElem.innerText.replace(newVal, ''));
                let selection = window.getSelection();
                if (selection.rangeCount > 0) {
                    let textNode = selection.focusNode;
                    selection.collapse(textNode, selection.rangeCount);
                }
            }
            // The user toggled the control to an editable state with the placholder text in place; present placeholder text
            if (this._rootElem.innerText.length === newVal.length) {
                addPlaceholderText(newVal);
            }
            // The user hit delete/backspace with the placeholder text up; present placeholder text
        } else if (this._rootElem.innerText.length === (newVal.length - 1)) {
            if (newVal.indexOf(this._rootElem.innerText) !== -1) {
                addPlaceholderText(newVal);
            }
        }
    }

    /**
     * Toggle or set the dark theme state
     * @param newVal {Boolean}
     * @private
     */
    _setDarkTheme(newVal){
        if (newVal === true) {
            this._rootElem.classList.add('dark');
        } else {
            this._rootElem.classList.remove('dark');
        }
    }

    /**
     * Set or remove focus on the textfield
     * @param newVal {Boolean}
     * @private
     */
    _setFocus(newVal){
        if (newVal === true) {
            this._setEditing(true);
        } else {
            this._setEditing(false);
        }
    }

    /**
     * Set an attribute on the native control
     * @param attrName {String} - The name of the HTML attribute to be set on the native control
     * @param attrValue {String|Undefined} - The value of the HTML attribute to be set on the native control
     * @private
     */
    _setAttribute(attrName, attrValue){
        if (!_.isUndefined(attrValue)) {
            this._rootElem.setAttribute(attrName, attrValue);
        } else {
            this._rootElem.removeAttribute(attrName);
        }
    }

    /*******************************************/
    /*  END PRIVATE API METHODS */
    /*******************************************/
}

module.exports = EditableTextControlRenderer;